/**
 * New Members — the shapes `adminNewMembers` returns, and the small readings of
 * them that the page, the detail panel and the insights all share.
 */

export interface NewMemberEvent {
  eventId: string;
  title: string;
  link: string;
  programme: string;
  typeLabel: string;
  startDate: string;
  /** On her clock; absent when we don't know her timezone. */
  when?: string | null;
  /** Whose clock `when` is on: "profile", "signup", or "". */
  clock: string;
  hostName?: string | null;
  why: string;
  afterPass: boolean;
  outsideHerDay: boolean;
  levelMismatch: boolean;
}

export interface NewMemberType {
  programme: string;
  label: string;
  tried: boolean;
  event?: NewMemberEvent | null;
}

export interface NewMemberSuggestion {
  event?: NewMemberEvent | null;
  invitation?: NewMemberEvent | null;
  byType: NewMemberType[];
  untried: string[];
  holds: number;
  resourceTitle: string;
  resourceLink: string;
}

export interface NewMemberAngle {
  key: string;
  label: string;
  message: string;
}

export interface NewMember {
  userId: string;
  name: string;
  firstName: string;
  email: string;
  status: string;
  statusLabel: string;
  signedUpAt: string;
  ageDays: number;
  timezone?: string | null;
  selectedTimezone?: string | null;
  speakingLevel?: string | null;
  guestTokensLeft?: number | null;
  guestDaysLeft?: number | null;
  guestPassLive: boolean;
  eventsAttended: number;
  acceptedUpcoming: number;
  priority?: string | null;
  priorityLabel?: string | null;
  challenge?: string | null;
  challengeLabel?: string | null;
  readiness?: string | null;
  readinessLabel?: string | null;
  firstEventSkipped: boolean;
  firstEventId?: string | null;
  firstEventTitle?: string | null;
  firstEventStart?: string | null;
  firstEventWhen?: string | null;
  firstEventState?: string | null;
  redFlag?: string | null;
  redFlagLabel?: string | null;
  signupState: string;
  suggestion: NewMemberSuggestion;
  angles: NewMemberAngle[];
  defaultAngle: string;
  message: string;
}

export interface NewMembersResult {
  days: number;
  total: number;
  generatedAt: string;
  members: NewMember[];
}

/** Where her Guest Pass stands, if she came in on one. */
export type PassKind = "live" | "spent" | "ended" | "member";

export const PASS_LABELS: Record<PassKind, string> = {
  live: "Guest Pass running",
  spent: "Used all 3 events",
  ended: "Pass ended",
  member: "Not a Guest",
};

export const passKind = (member: NewMember): PassKind => {
  if (member.status === "guest") {
    return member.guestTokensLeft === 0 ? "spent" : "live";
  }
  // A pass that has ended without her joining. One that converted is a member's.
  return member.guestTokensLeft != null && member.status === "gone" ? "ended" : "member";
};

export type FirstEventKind = "booked" | "skipped" | "ahead" | "came" | "missed";

export const FIRST_EVENT_LABELS: Record<FirstEventKind, string> = {
  booked: "Booked one",
  skipped: "Skipped booking",
  ahead: "Still to come",
  came: "Came to it",
  missed: "Missed it",
};

/** What she did about a first event at signup, and what became of it. */
export const firstEventKinds = (member: NewMember): FirstEventKind[] => {
  if (member.firstEventSkipped) {
    return ["skipped"];
  }
  if (!member.firstEventId) {
    return [];
  }
  const state = member.firstEventState;
  return state === "ahead" || state === "came" || state === "missed"
    ? ["booked", state]
    : ["booked"];
};

const STATUS_COLOURS: Record<string, "success" | "error" | "warning" | "info"> = {
  active: "success",
  guest: "info",
  non_renewing: "warning",
  paused: "warning",
  gone: "error",
};

export const statusColour = (
  status: string,
): "success" | "error" | "warning" | "info" | "default" => STATUS_COLOURS[status] ?? "default";

export const signedUpText = (ageDays: number): string => {
  if (ageDays === 0) {
    return "today";
  }
  return ageDays === 1 ? "yesterday" : `${ageDays} days ago`;
};

/** Event titles carry emoji; a table reads better without them. */
export const cleanTitle = (title?: string | null): string =>
  (title ?? "")
    .replace(/\p{Extended_Pictographic}️?/gu, "")
    .replace(/\s+/g, " ")
    .trim();

/** She no longer has a live membership or pass. */
export const hasLeft = (member: NewMember): boolean => member.status === "gone";
